import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldOption } from '../../types/protocol';

interface SelectOneFieldProps {
  field: string;
  options: FieldOption[];
  defaultValue?: string | number;
  help?: string;
  allowCreate?: boolean;
  searchable?: boolean;
  searchTool?: string;
  stepId?: string;
  onSearch?: (stepId: string, query: string) => void;
  onSubmit: (value: unknown) => void;
}

export function SelectOneField({ field: _field, options, defaultValue, help, allowCreate, searchable, searchTool, stepId, onSearch, onSubmit }: SelectOneFieldProps): React.ReactElement {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | number | null>(defaultValue ?? null);
  const [creating, setCreating] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [filterText, setFilterText] = useState('');
  const optionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const isServerSearch = !!searchTool && !!stepId && !!onSearch;
  // Capture pre-loaded options at mount so we can restore them when the user clears search
  const [preloadedOptions] = useState(options);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setFilterText(text);

      if (isServerSearch) {
        clearTimeout(debounceRef.current);
        if (text.trim().length >= 2) {
          debounceRef.current = setTimeout(() => {
            onSearch(stepId, text.trim());
          }, 300);
        }
      }
    },
    [isServerSearch, onSearch, stepId],
  );

  // Clean up debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (creating && newValue.trim()) {
        onSubmit({ _create: true, name: newValue.trim() });
      } else if (selected !== null) {
        onSubmit(selected);
      }
    },
    [selected, creating, newValue, onSubmit],
  );

  const showSearch = searchable || options.length > 8;
  // Server-side search: show pre-loaded options initially, server results after typing.
  // Client-side: filter locally by label/description.
  const filteredOptions = useMemo(() => {
    if (isServerSearch) {
      // Before server search triggers (< 2 chars), client-filter the pre-loaded options
      if (filterText.trim().length < 2) {
        const source = preloadedOptions;
        if (!filterText.trim()) return source;
        const lower = filterText.toLowerCase();
        return source.filter(
          (o) => o.label.toLowerCase().includes(lower) || o.description?.toLowerCase().includes(lower),
        );
      }
      return options; // Server-filtered results
    }
    if (!filterText.trim()) return options;
    const lower = filterText.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(lower) || o.description?.toLowerCase().includes(lower),
    );
  }, [options, filterText, isServerSearch, preloadedOptions]);

  // Select and submit on Enter key for an option
  const handleOptionKeyDown = useCallback(
    (e: React.KeyboardEvent, value: string | number) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setSelected(value);
        setCreating(false);
        onSubmit(value);
      }
    },
    [onSubmit],
  );

  // Arrow key navigation in the options list
  const handleOptionsKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const container = optionsRef.current;
      if (!container) return;
      const items = Array.from(container.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
      const idx = items.findIndex((el) => el === document.activeElement || el.parentElement === document.activeElement);
      let next = idx;
      if (e.key === 'ArrowDown') next = Math.min(idx + 1, items.length - 1);
      else next = Math.max(idx - 1, 0);
      items[next]?.focus();
    },
    [],
  );

  // Auto-focus the selected option (or first option) on mount
  useEffect(() => {
    const container = optionsRef.current;
    if (!container) return;
    const checked = container.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    if (checked) {
      checked.focus();
    } else {
      const first = container.querySelector<HTMLInputElement>('input[type="radio"]');
      first?.focus();
    }
  }, []);

  // Handle Enter from anywhere in the form (e.g. search input) to submit
  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !creating && selected !== null) {
        e.preventDefault();
        onSubmit(selected);
      }
    },
    [creating, selected, onSubmit],
  );

  return (
    <form className="sp-field sp-field-select-one" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
      {showSearch && (
        <input
          className="sp-field-filter"
          type="text"
          value={filterText}
          onChange={handleFilterChange}
          placeholder={isServerSearch ? t('fields.typeToSearch') : t('fields.search')}
          autoFocus={isServerSearch}
        />
      )}
      <div
        className="sp-field-options sp-field-options-scroll"
        ref={optionsRef}
        role="radiogroup"
        onKeyDown={handleOptionsKeyDown}
      >
        {filteredOptions.map((opt) => (
          <label key={opt.value} className={`sp-option${selected === opt.value ? ' sp-option-selected' : ''}`}>
            <input
              type="radio"
              name="select-one"
              value={String(opt.value)}
              checked={selected === opt.value}
              onChange={() => {
                setSelected(opt.value);
                setCreating(false);
              }}
              onKeyDown={(e) => handleOptionKeyDown(e, opt.value)}
            />
            <span className="sp-option-label">{opt.label}</span>
            {opt.description && <span className="sp-option-desc">{opt.description}</span>}
          </label>
        ))}
        {filteredOptions.length === 0 && (
          <div className="sp-option-empty">
            {isServerSearch && !filterText.trim()
              ? t('fields.typeToSearch')
              : t('fields.noMatches')}
          </div>
        )}
      </div>
      {allowCreate && (
        <button
          type="button"
          className="sp-create-btn"
          onClick={() => {
            setCreating(true);
            setSelected(null);
          }}
        >
          {t('fields.createNew')}
        </button>
      )}
      {creating && (
        <input
          className="sp-field-input sp-create-input"
          type="text"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={t('fields.enterNewName')}
          autoFocus
        />
      )}
      {help && <span className="sp-field-help">{help}</span>}
      <button
        className="sp-field-submit"
        type="submit"
        disabled={creating ? !newValue.trim() : selected === null}
      >
        {creating ? t('fields.create') : t('fields.submit')}
      </button>
    </form>
  );
}
